/* AYU-Kompass Section – Gold + Olive Design
 * Dark section with CSS/HTML iPhone mockup
 * INTERAKTIV: Klick auf Feature zeigt passenden Chat-Verlauf im Mockup
 * Mobile: Beim Klick scrollt es automatisch zum Mockup
 */
import { IMAGES } from "@/lib/constants";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Compass, BookOpen, TrendingUp, Calendar, Star, MessageCircle } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useI18n } from "@/contexts/I18nContext";

/* ── Feature definitions with matching chat conversations ── */
const features = [
  {
    icon: MessageCircle,
    textKey: "landing.kompass.feature1",
    chat: [
      { type: "date" as const, text: "Mittwoch, 5. März" },
      { type: "user" as const, text: "Ich fühle mich seit Tagen so schwer und antriebslos. Liegt das an meiner Ernährung?" },
      { type: "bot" as const, label: "AYU REFLEXION", text: "Das klingt, als ob dein Körper dir ein Signal sendet. Bei deinem Phänotyp ", highlight: "Dicker Stabiler / Heller Trockener" },
      { type: "bot" as const, text: "kann sich Schwere besonders in den Übergangszeiten zeigen. Dein Agni (Verdauungsfeuer) braucht jetzt Unterstützung — warme, leichte Speisen mit Ingwer und Kreuzkümmel." },
      { type: "user" as const, text: "Das klingt machbar. Danke!" },
    ],
  },
  {
    icon: Compass,
    textKey: "landing.kompass.feature2",
    chat: [
      { type: "date" as const, text: "Morgen-Check-in" },
      { type: "bot" as const, label: "TAGESEMPFEHLUNG", text: "Guten Morgen! Basierend auf deinem Phänotyp und der aktuellen Jahreszeit:" },
      { type: "bot" as const, text: "• Warmes Wasser mit Zitrone & Ingwer\n• 10 Min. Sonnengruß\n• Mittags: Kitchari mit Kurkuma\n• Abends: Leichte Suppe vor 19 Uhr" },
      { type: "user" as const, text: "Ich habe gestern Abend warm gegessen und heute Morgen fühle ich mich schon leichter!" },
      { type: "bot" as const, label: "AYU REFLEXION", text: "Wunderbar! Dein Körper reagiert positiv. Bleib heute bei warmen Mahlzeiten und trinke über den Tag verteilt Ingwertee." },
    ],
  },
  {
    icon: BookOpen,
    textKey: "landing.kompass.feature3",
    chat: [
      { type: "date" as const, text: "Tagebuch-Eintrag" },
      { type: "user" as const, text: "Heute war ein guter Tag. Habe mich an den Ernährungsplan gehalten und 30 Min. spaziert." },
      { type: "bot" as const, label: "TAGEBUCH", text: "Toll! Ich habe deinen Eintrag gespeichert. Deine Konsistenz der letzten 7 Tage:" },
      { type: "bot" as const, text: "• Ernährung: 6/7 Tage ✓\n• Bewegung: 5/7 Tage ✓\n• Schlaf: Ø 7,2 Stunden\n• Stimmung: Aufwärtstrend ↑" },
      { type: "user" as const, text: "Das motiviert mich weiterzumachen!" },
    ],
  },
  {
    icon: TrendingUp,
    textKey: "landing.kompass.feature4",
    chat: [
      { type: "date" as const, text: "Monatsübersicht" },
      { type: "bot" as const, label: "FORTSCHRITT", text: "Dein Monatsbericht für Februar ist da! Hier sind deine Highlights:" },
      { type: "bot" as const, text: "• Energie-Level: +35% gegenüber Januar\n• Verdauung: Deutlich verbessert\n• Schlafqualität: Von 5,8 auf 7,4\n• Stresslevel: -20%" },
      { type: "user" as const, text: "Wow, das hätte ich nicht erwartet! Was kann ich im März noch verbessern?" },
      { type: "bot" as const, label: "AYU REFLEXION", text: "Fokussiere dich auf regelmäßige Abendroutinen. Bei deinem Phänotyp hilft eine feste Schlafenszeit um 22:30 besonders." },
    ],
  },
  {
    icon: Calendar,
    textKey: "landing.kompass.feature5",
    chat: [
      { type: "date" as const, text: "Frühlings-Update" },
      { type: "bot" as const, label: "SAISONALER WECHSEL", text: "Der Frühling beginnt! Für deinen Phänotyp bedeutet das:" },
      { type: "bot" as const, text: "• Stoffwechsel aktivieren: Leichtere Kost bevorzugen\n• Mehr Bitterstoffe (Löwenzahn, Rucola)\n• Morgens: Trockenbürsten vor dem Duschen\n• Bewegung: Intensität steigern" },
      { type: "user" as const, text: "Soll ich auch meine Abendroutine anpassen?" },
      { type: "bot" as const, label: "AYU REFLEXION", text: "Ja! Im Frühling kannst du abends auf schwere Öle verzichten. Stattdessen: leichtes Sesamöl und kürzere Meditation (10 statt 20 Min.)." },
    ],
  },
  {
    icon: Star,
    textKey: "landing.kompass.feature6",
    chat: [
      { type: "date" as const, text: "Wochenplan" },
      { type: "bot" as const, label: "WOCHENRITUALE", text: "Deine personalisierten Rituale für diese Woche:" },
      { type: "bot" as const, text: "• Mo: Ölziehen + Yoga (20 Min.)\n• Mi: Abhyanga (Selbstmassage)\n• Fr: Digital Detox ab 20 Uhr\n• So: Meal Prep für die Woche" },
      { type: "user" as const, text: "Kann ich das Ölziehen auch abends machen?" },
      { type: "bot" as const, label: "AYU REFLEXION", text: "Morgens ist ideal, da es die Nacht-Toxine entfernt. Aber wenn es zeitlich nicht passt: Abends vor dem Zähneputzen ist die zweitbeste Option." },
    ],
  },
];

type ChatMessage = {
  type: "user" | "bot" | "date";
  text?: string;
  label?: string;
  highlight?: string;
};

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div
        className="px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5"
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.04)",
        }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "0ms", animationDuration: "0.8s" }} />
        <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "150ms", animationDuration: "0.8s" }} />
        <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "300ms", animationDuration: "0.8s" }} />
      </div>
    </div>
  );
}

function ChatMessageBubble({ msg, visible }: { msg: ChatMessage; visible: boolean }) {
  if (msg.type === "date") {
    return (
      <div
        className="text-center transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(8px)" }}
      >
        <span className="text-[10px] text-white/30 px-3 py-1 rounded-full bg-white/5 font-medium">
          {msg.text}
        </span>
      </div>
    );
  }

  if (msg.type === "user") {
    return (
      <div
        className="flex justify-end transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.95)" }}
      >
        <div
          className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-[11.5px] leading-[1.5]"
          style={{ background: "oklch(0.52 0.09 128)", color: "white" }}
        >
          {msg.text}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex justify-start transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.95)" }}
    >
      <div
        className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[11.5px] leading-[1.55]"
        style={{
          background: "rgba(255,255,255,0.08)",
          color: "oklch(0.88 0.01 80)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {msg.label && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[oklch(0.52_0.09_128)] text-[9px]">&#10022;</span>
            <span className="text-[oklch(0.78_0.11_85)] text-[9px] font-semibold tracking-wider uppercase">{msg.label}</span>
          </div>
        )}
        {msg.highlight ? (
          <span>
            {msg.text}
            <strong style={{ color: "oklch(0.78 0.11 85)" }}>{msg.highlight}</strong>
          </span>
        ) : (
          <span style={{ whiteSpace: "pre-line" }}>{msg.text}</span>
        )}
      </div>
    </div>
  );
}

function PhoneMockup({ activeChat, animKey }: { activeChat: ChatMessage[]; animKey: number }) {
  const { t } = useI18n();
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  // Clear all pending timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
  }, []);

  // Run animation whenever activeChat or animKey changes
  useEffect(() => {
    clearAllTimeouts();
    setVisibleCount(0);
    setShowTyping(false);

    let currentIndex = 0;

    const showNext = () => {
      if (currentIndex >= activeChat.length) {
        setShowTyping(true);
        const t = setTimeout(scrollToBottom, 100);
        timeoutsRef.current.push(t);
        return;
      }

      const msg = activeChat[currentIndex];
      const isBot = msg.type === "bot";
      const isDate = msg.type === "date";

      if (isBot) {
        setShowTyping(true);
        const t1 = setTimeout(scrollToBottom, 50);
        timeoutsRef.current.push(t1);

        const typingDuration = Math.min(600 + (msg.text?.length || 0) * 10, 1800);

        const t2 = setTimeout(() => {
          setShowTyping(false);
          currentIndex++;
          setVisibleCount(currentIndex);
          const t3 = setTimeout(scrollToBottom, 50);
          timeoutsRef.current.push(t3);
          const t4 = setTimeout(showNext, 350);
          timeoutsRef.current.push(t4);
        }, typingDuration);
        timeoutsRef.current.push(t2);
      } else if (isDate) {
        currentIndex++;
        setVisibleCount(currentIndex);
        const t1 = setTimeout(scrollToBottom, 50);
        timeoutsRef.current.push(t1);
        const t2 = setTimeout(showNext, 500);
        timeoutsRef.current.push(t2);
      } else {
        currentIndex++;
        setVisibleCount(currentIndex);
        const t1 = setTimeout(scrollToBottom, 50);
        timeoutsRef.current.push(t1);
        const t2 = setTimeout(showNext, 600);
        timeoutsRef.current.push(t2);
      }
    };

    const t = setTimeout(showNext, 400);
    timeoutsRef.current.push(t);

    return () => clearAllTimeouts();
  }, [activeChat, animKey, scrollToBottom, clearAllTimeouts]);

  return (
    <div className="relative mx-auto w-[300px] max-w-full">
      {/* Phone Frame */}
      <div
        className="relative rounded-[40px] overflow-hidden shadow-2xl"
        style={{
          background: "linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 50%, #111 100%)",
          padding: "12px",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div
          className="relative rounded-[30px] overflow-hidden"
          style={{ background: "#0f1a0f", height: "580px" }}
        >
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 pb-1 relative">
            <span className="text-[11px] font-semibold text-white/80">9:41</span>
            <div
              className="w-[90px] h-[28px] bg-black rounded-full"
              style={{ position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)" }}
            />
            <div className="flex items-center gap-1">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="white" opacity="0.8">
                <rect x="0" y="6" width="2.5" height="4" rx="0.5" />
                <rect x="3.5" y="4" width="2.5" height="6" rx="0.5" />
                <rect x="7" y="2" width="2.5" height="8" rx="0.5" />
                <rect x="10.5" y="0" width="2.5" height="10" rx="0.5" />
              </svg>
              <svg width="14" height="10" viewBox="0 0 24 16" fill="white" opacity="0.8">
                <path d="M1 5.5C4.5 1.5 9 0 12 0s7.5 1.5 11 5.5l-2 2C18 4 15 2.5 12 2.5S6 4 3 7.5l-2-2z" />
                <path d="M5 9.5C7 7 9.5 6 12 6s5 1 7 3.5l-2 2C15.5 9.5 14 9 12 9s-3.5.5-5 2.5l-2-2z" />
                <circle cx="12" cy="14" r="2" />
              </svg>
              <svg width="22" height="10" viewBox="0 0 25 11" fill="none" stroke="white" opacity="0.8">
                <rect x="0.5" y="0.5" width="20" height="10" rx="2" strokeWidth="1" />
                <rect x="22" y="3" width="2" height="5" rx="1" fill="white" opacity="0.4" />
                <rect x="1.5" y="1.5" width="14" height="8" rx="1" fill="white" opacity="0.8" />
              </svg>
            </div>
          </div>

          {/* Chat Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-[16px]">&#8249;</span>
            </div>
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0" style={{ background: "linear-gradient(135deg, oklch(0.52 0.09 128), oklch(0.42 0.09 128))" }}>
              <div className="w-full h-full flex items-center justify-center">
                <img src={IMAGES.logo} alt="AYU" className="w-5 h-5 object-contain" />
              </div>
            </div>
            <div>
              <div className="text-white text-[13px] font-semibold leading-tight">{t("landing.kompass.chatHeader")}</div>
              <div className="text-[oklch(0.52_0.09_128)] text-[10px] leading-tight">{t("landing.kompass.chatSubheader")}</div>
            </div>
            <div className="ml-auto w-2 h-2 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse" />
          </div>

          {/* Chat Messages */}
          <div
            ref={chatContainerRef}
            className="px-3 py-4 space-y-3 overflow-y-auto"
            style={{
              height: "calc(100% - 130px)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <style>{`div::-webkit-scrollbar { display: none; }`}</style>

            {activeChat.map((msg, i) => (
              <ChatMessageBubble key={`${animKey}-${i}`} msg={msg} visible={i < visibleCount} />
            ))}

            {showTyping && <TypingIndicator />}

            <div className="h-12" />
          </div>

          {/* Input bar */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-6 pt-3" style={{ background: "linear-gradient(to top, #0f1a0f 70%, transparent)" }}>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <span className="text-white/30 text-[12px] flex-1">{t("landing.kompass.inputPlaceholder")}</span>
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "oklch(0.52 0.09 128)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                </svg>
              </div>
            </div>
            <div className="w-[120px] h-[4px] bg-white/20 rounded-full mx-auto mt-3" />
          </div>
        </div>
      </div>

      {/* Glow under phone */}
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[80%] h-8 bg-[oklch(0.52_0.09_128/0.15)] blur-xl rounded-full" />
    </div>
  );
}

export default function KompassSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();
  const [activeFeature, setActiveFeature] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const phoneRef = useRef<HTMLDivElement>(null);

  const handleFeatureClick = (index: number) => {
    setActiveFeature(index);
    setAnimKey(prev => prev + 1);

    // On mobile, scroll to the phone mockup
    if (window.innerWidth < 1024 && phoneRef.current) {
      phoneRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Auto-start first animation when section becomes visible
  useEffect(() => {
    if (isVisible) {
      setAnimKey(prev => prev + 1);
    }
  }, [isVisible]);

  return (
    <section id="kompass" className="section-dark py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 mandala-overlay pointer-events-none" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.78_0.11_85)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.kompass.badge")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.93_0.015_80)] mt-4 mb-6">
              {t("landing.kompass.heading")}
            </h2>

            <p className="text-lg text-[oklch(0.7_0.02_80)] leading-relaxed mb-4" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.kompass.p1")}
            </p>

            <p className="text-lg text-[oklch(0.7_0.02_80)] leading-relaxed mb-8" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.kompass.p2")}
            </p>

            <div className="space-y-2">
              {features.map((feature, i) => (
                <button
                  key={i}
                  onClick={() => handleFeatureClick(i)}
                  className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-300 text-left cursor-pointer ${
                    activeFeature === i
                      ? "bg-[oklch(0.52_0.09_128/0.15)] ring-1 ring-[oklch(0.52_0.09_128/0.3)]"
                      : "hover:bg-[oklch(0.25_0.02_280/0.5)]"
                  } ${
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                  }`}
                  style={{ transitionDelay: `${i * 100 + 300}ms`, transitionDuration: "500ms" }}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${
                    activeFeature === i
                      ? "bg-[oklch(0.52_0.09_128/0.3)]"
                      : "bg-[oklch(0.52_0.09_128/0.15)]"
                  }`}>
                    <feature.icon className={`transition-colors duration-300 ${
                      activeFeature === i
                        ? "text-[oklch(0.78_0.11_85)]"
                        : "text-[oklch(0.52_0.09_128)]"
                    }`} size={18} />
                  </div>
                  <span className={`transition-colors duration-300 ${
                    activeFeature === i
                      ? "text-[oklch(0.93_0.015_80)] font-medium"
                      : "text-[oklch(0.85_0.01_80)]"
                  }`} style={{ fontFamily: "var(--font-body)" }}>
                    {t(feature.textKey as any)}
                  </span>
                  {activeFeature === i && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Right: Phone Mockup with interactive chat */}
          <div
            ref={phoneRef}
            className={`transition-all duration-700 delay-300 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="relative flex justify-center">
              <div className="absolute inset-0 bg-[oklch(0.52_0.09_128/0.06)] rounded-full blur-[80px]" />
              <div className="absolute inset-10 bg-[oklch(0.78_0.11_85/0.04)] rounded-full blur-[60px]" />

              <PhoneMockup activeChat={features[activeFeature].chat} animKey={animKey} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
